import React, { useContext, createContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  SidebarContainer,
  SidebarHeader,
  SidebarMenu,
  SidebarGroup,
  SidebarItem,
  SidebarFooter,
  SidebarLink,
  SidebarTitle,
  SidebarUserTrigger,
  SidebarUserInfo,
  SidebarUserIdentity,
  SidebarIcon,
  SidebarText,
  SidebarUserName,
  SidebarUserEmail,
  SidebarUserMenuIndicator,
  SidebarUserMenu,
  SidebarUserMenuDivider,
  SidebarUserMenuItem,
  SidebarUserMenuLink,
  SidebarUserSkeleton,
} from "./Sidebar.styles";
import {
  courseImg,
  statsImg,
  studentIcon,
  userProfileIcon as UserProfileIcon,
  homeworkIcon,
  refIcon,
  paymentIcon,
  settingsIcon as SettingsIcon,
  helpIcon,
  docTempIcon,
  leadsIcon,
  calendarIcon,
  dropDownArrowIcon as DropDownArrowIcon,
  logoutIcon as LogoutIcon,
} from "assets/icons";
import { useUser } from "../../context/UserContext";
import { useAuth } from "../../context/AuthContext";

const SidebarContext = createContext({ expanded: true });

export const SidebarMenuProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [expanded] = useState(true);

  return (
    <SidebarContext.Provider value={{ expanded }}>
      {children}
    </SidebarContext.Provider>
  );
};

const SidebarItemComponent: React.FC<{
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  text: string;
  to: string;
}> = ({ icon, text, to }) => {
  const { expanded } = useContext(SidebarContext);
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <SidebarItem>
      <Link to={to}>
        <SidebarLink isActive={isActive}>
          <SidebarIcon as={icon} aria-hidden="true" />
          {expanded && <SidebarText>{text}</SidebarText>}
        </SidebarLink>
      </Link>
    </SidebarItem>
  );
};

const Sidebar: React.FC = () => {
  const { user } = useUser();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const footerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const userName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(" ")
    : "";

  useEffect(() => {
    if (!userMenuOpen) return undefined;

    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();

    const handlePointerDown = (event: MouseEvent) => {
      if (!footerRef.current?.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setUserMenuOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [userMenuOpen]);

  const handleUserMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (!items.length) return;

    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    const focusAt = (index: number) => {
      event.preventDefault();
      items[index]?.focus();
    };

    if (event.key === "ArrowDown") {
      focusAt(currentIndex < 0 ? 0 : (currentIndex + 1) % items.length);
      return;
    }
    if (event.key === "ArrowUp") {
      focusAt(currentIndex <= 0 ? items.length - 1 : currentIndex - 1);
      return;
    }
    if (event.key === "Home") {
      focusAt(0);
      return;
    }
    if (event.key === "End") {
      focusAt(items.length - 1);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setUserMenuOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (event.key === "Tab") {
      setUserMenuOpen(false);
    }
  };

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate("/login");
  };

  return (
    <SidebarContainer>
      <SidebarHeader>LISRENT</SidebarHeader>
      <SidebarMenu>
        <SidebarGroup>
          <SidebarTitle>Курсы и статистика</SidebarTitle>
          <SidebarItemComponent icon={courseImg} text="Мои курсы" to="/courses" />
          <SidebarItemComponent icon={statsImg} text="Статистика" to="/statistics" />
        </SidebarGroup>
        <SidebarGroup>
          <SidebarTitle>Учеба</SidebarTitle>
          <SidebarItemComponent icon={leadsIcon} text="Лиды" to="/leads" />
          <SidebarItemComponent icon={studentIcon} text="Ученики" to="/students" />
          <SidebarItemComponent icon={calendarIcon} text="Календарь" to="/events" />
          <SidebarItemComponent
            icon={homeworkIcon}
            text="Домашние задания"
            to="/homework"
          />
        </SidebarGroup>
        <SidebarGroup>
          <SidebarTitle>Оплата и акции</SidebarTitle>
          <SidebarItemComponent icon={refIcon} text="Реферальная программа" to="/referals" />
          <SidebarItemComponent icon={paymentIcon} text="Оплата" to="/payment" />
        </SidebarGroup>
        <SidebarGroup>
          <SidebarTitle>Другое</SidebarTitle>
          <SidebarItemComponent icon={docTempIcon} text="Документы" to="/documents" />
          <SidebarItemComponent icon={helpIcon} text="Поддержка" to="/support-requests" />
        </SidebarGroup>
      </SidebarMenu>
      <SidebarFooter ref={footerRef}>
        <SidebarUserTrigger
          ref={triggerRef}
          type="button"
          aria-label="Профиль пользователя"
          aria-haspopup="menu"
          aria-expanded={userMenuOpen}
          onClick={() => setUserMenuOpen((current) => !current)}
        >
          <SidebarUserInfo>
            <SidebarUserIdentity>
              {user ? (
                <>
                  <SidebarUserName title={userName}>{userName}</SidebarUserName>
                  <SidebarUserEmail title={user.email}>{user.email}</SidebarUserEmail>
                </>
              ) : (
                <>
                  <SidebarUserSkeleton
                    data-testid="sidebar-user-name-skeleton"
                    $width={120}
                    $height={13}
                    aria-hidden="true"
                  />
                  <SidebarUserSkeleton
                    data-testid="sidebar-user-email-skeleton"
                    $width={150}
                    $height={11}
                    aria-hidden="true"
                  />
                </>
              )}
            </SidebarUserIdentity>
            <SidebarUserMenuIndicator
              as={DropDownArrowIcon}
              $open={userMenuOpen}
              aria-hidden="true"
            />
          </SidebarUserInfo>
        </SidebarUserTrigger>
        {userMenuOpen && (
          <SidebarUserMenu
            ref={menuRef}
            role="menu"
            onKeyDown={handleUserMenuKeyDown}
          >
            <SidebarUserMenuLink
              to="/profile"
              role="menuitem"
              onClick={() => setUserMenuOpen(false)}
            >
              <UserProfileIcon aria-hidden="true" />
              Личный кабинет
            </SidebarUserMenuLink>
            <SidebarUserMenuLink
              to="/settings"
              role="menuitem"
              onClick={() => setUserMenuOpen(false)}
            >
              <SettingsIcon aria-hidden="true" />
              Настройки
            </SidebarUserMenuLink>
            <SidebarUserMenuDivider role="separator" />
            <SidebarUserMenuItem
              type="button"
              role="menuitem"
              $danger
              onClick={handleLogout}
            >
              <LogoutIcon aria-hidden="true" />
              Выйти
            </SidebarUserMenuItem>
          </SidebarUserMenu>
        )}
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default Sidebar;
